import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import { useGetQuestionCategories, useGetInterviewQuestions } from '@/reactQuery/get';

interface ComparisonCategoryChartProps {
  questionId: string;
  questionText?: string;
  filterIdA: string;
  filterIdB: string;
  filterAName: string;
  filterBName: string;
}

interface MergedCategory {
  id: string;
  label: string;
  countA: number;
  countB: number;
  percentageA: number;
  percentageB: number;
}

/**
 * Resolves a comparison question_id (which may be AI-generated text) to the
 * real UUID by matching against the project's interview questions.
 *
 * The previous implementation was too permissive: a 30-char prefix match
 * collides on questions sharing natural openings ("How satisfied are you
 * with..."), and a fuzzy threshold of 2 matched words out of N accepted
 * nearly any overlapping vocabulary. Both failure modes silently pointed
 * the chart at the WRONG question's category data, with no visual
 * indication to the user.
 *
 * Tightened strategy — prefer returning null (no chart) to returning a
 * wrong match:
 *   1. Exact normalized-text match wins immediately.
 *   2. Long-prefix match (≥60 chars) accepted only when exactly ONE
 *      real question contains that prefix.
 *   3. Significant-word overlap accepted only when the best match has
 *      ≥70% word coverage AND beats the runner-up by ≥20pp (no ties).
 *   4. Otherwise null — the parent hides the chart on null.
 *
 * Long term: the backend should return stable question UUIDs in the
 * comparison result so this resolver is unnecessary. Until then, this
 * errs on the side of "missing chart" rather than "misleading chart".
 */
function resolveQuestionId(
  questionId: string,
  questionText: string | undefined,
  interviewQuestions: Array<{ id: string; question: string }> | undefined,
): string | null {
  if (!interviewQuestions) return null;

  // If questionId is already a UUID, use it directly.
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  if (uuidRegex.test(questionId)) return questionId;

  if (!questionText) return null;
  const searchText = questionText.toLowerCase().trim();
  if (!searchText) return null;

  // 1. Exact normalized match — strongest signal, immediate return.
  const exact = interviewQuestions.find(
    (q) => q.question.toLowerCase().trim() === searchText,
  );
  if (exact) return exact.id;

  // 2. Unambiguous long-prefix match. 60 chars is long enough to
  // distinguish natural question openings while short enough to still
  // match when the AI paraphrased the tail.
  const PREFIX_LEN = 60;
  if (searchText.length >= PREFIX_LEN) {
    const prefix = searchText.substring(0, PREFIX_LEN);
    const prefixMatches = interviewQuestions.filter((q) =>
      q.question.toLowerCase().includes(prefix),
    );
    if (prefixMatches.length === 1) return prefixMatches[0].id;
  }

  // 3. Significant-word overlap, measured as proportion. Requires a
  // clear winner: best ≥70% coverage AND at least 20pp ahead of the
  // runner-up. Single-word questions fall through to null.
  const searchWords = searchText.split(/\s+/).filter((w) => w.length > 3);
  if (searchWords.length >= 3) {
    const scored = interviewQuestions.map((q) => {
      const realText = q.question.toLowerCase();
      const matched = searchWords.filter((w) => realText.includes(w)).length;
      return { id: q.id, score: matched / searchWords.length };
    });
    scored.sort((a, b) => b.score - a.score);
    const [first, second] = scored;
    if (
      first &&
      first.score >= 0.7 &&
      (!second || first.score - second.score >= 0.2)
    ) {
      return first.id;
    }
  }

  // No confident match — caller hides the chart.
  return null;
}

function ComparisonCategoryChart({
  questionId,
  questionText,
  filterIdA,
  filterIdB,
  filterAName,
  filterBName,
}: ComparisonCategoryChartProps) {
  const { project_id: projectId } = useParams();
  const { lang } = useTranslation();

  // Fetch the project's questions to resolve AI-generated question IDs to real UUIDs
  const { data: interviewQuestions } = useGetInterviewQuestions(null, projectId);

  const resolvedId = useMemo(
    () => resolveQuestionId(questionId, questionText, interviewQuestions),
    [questionId, questionText, interviewQuestions],
  );

  const { data: catDataA, isPending: isPendingA } = useGetQuestionCategories(
    filterIdA,
    projectId,
    resolvedId || undefined,
  );
  const { data: catDataB, isPending: isPendingB } = useGetQuestionCategories(
    filterIdB,
    projectId,
    resolvedId || undefined,
  );

  // Don't render anything if we can't resolve the question ID
  if (!resolvedId) return null;

  if (isPendingA || isPendingB) {
    return (
      <div className="flex items-center gap-2 py-3 text-sm text-neutral-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {lang.get('msg.loadingCategories')}...
      </div>
    );
  }

  const categoriesA = catDataA?.categories || catDataA?.categories_multi || [];
  const categoriesB = catDataB?.categories || catDataB?.categories_multi || [];
  const conversationsA = catDataA?.conversations_num || 0;
  const conversationsB = catDataB?.conversations_num || 0;

  if (categoriesA.length === 0 && categoriesB.length === 0) {
    return null;
  }

  // Merge categories by ID — use A's order as primary, append any B-only categories
  const merged: MergedCategory[] = [];
  const bById = new Map(categoriesB.map((c) => [c.id, c]));
  const seen = new Set<string>();

  for (const catA of categoriesA) {
    const catB = bById.get(catA.id);
    merged.push({
      id: catA.id,
      label: catA.label,
      countA: catA.count || 0,
      countB: catB?.count || 0,
      percentageA: conversationsA > 0 ? ((catA.count || 0) / conversationsA) * 100 : 0,
      percentageB: conversationsB > 0 ? ((catB?.count || 0) / conversationsB) * 100 : 0,
    });
    seen.add(catA.id);
  }

  for (const catB of categoriesB) {
    if (!seen.has(catB.id)) {
      merged.push({
        id: catB.id,
        label: catB.label,
        countA: 0,
        countB: catB.count || 0,
        percentageA: 0,
        percentageB: conversationsB > 0 ? ((catB.count || 0) / conversationsB) * 100 : 0,
      });
    }
  }

  if (merged.length === 0) return null;

  const maxPercentage = Math.max(...merged.map((c) => Math.max(c.percentageA, c.percentageB)), 1);

  return (
    <div className="mt-4 pt-3 border-t border-neutral-100">
      <h4 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-3">
        {lang.get('msg.categories')}
      </h4>

      {/* Legend */}
      <div className="flex items-center gap-4 mb-3 text-xs text-neutral-500">
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-emerald-400" />
          {filterAName} ({conversationsA})
        </span>
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-2 rounded-sm bg-pink-300" />
          {filterBName} ({conversationsB})
        </span>
      </div>

      <div className="flex flex-col gap-2.5">
        {merged.map((cat) => {
          const widthA = (cat.percentageA / maxPercentage) * 100;
          const widthB = (cat.percentageB / maxPercentage) * 100;

          return (
            <div key={cat.id}>
              <p className="text-xs font-medium text-neutral-700 mb-1">{cat.label}</p>
              <div className="flex flex-col gap-0.5">
                {/* Segment A bar */}
                <div className="flex items-center gap-2 h-5">
                  <div className="flex-1 h-full bg-neutral-50 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 rounded-sm flex items-center px-2"
                      style={{
                        width: `${Math.max(widthA, 2)}%`,
                        minWidth: widthA > 0 ? '2.5rem' : undefined,
                      }}
                    >
                      {widthA > 0 && (
                        <span className="text-[10px] font-medium text-white whitespace-nowrap">
                          {cat.percentageA.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-neutral-400 tabular-nums w-6 text-right">
                    {cat.countA}
                  </span>
                </div>
                {/* Segment B bar */}
                <div className="flex items-center gap-2 h-5">
                  <div className="flex-1 h-full bg-neutral-50 rounded-sm overflow-hidden">
                    <div
                      className="h-full bg-pink-300 rounded-sm flex items-center px-2"
                      style={{
                        width: `${Math.max(widthB, 2)}%`,
                        minWidth: widthB > 0 ? '2.5rem' : undefined,
                      }}
                    >
                      {widthB > 0 && (
                        <span className="text-[10px] font-medium text-white whitespace-nowrap">
                          {cat.percentageB.toFixed(0)}%
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-neutral-400 tabular-nums w-6 text-right">
                    {cat.countB}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ComparisonCategoryChart;
